import React, { cloneElement, useState, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MenuItem from '@material-ui/core/MenuItem';
import ListSubheader from '@material-ui/core/ListSubheader';
import Paper from '@material-ui/core/Paper';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import MenuList from '@material-ui/core/MenuList';
import Popper from '@material-ui/core/Popper';
import Grow from '@material-ui/core/Grow';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import TPLink from 'components/TP-UI/TPLink';
import useStyles from './styles';

const TPMenu = ({
  header,
  children,
  placement = 'top-end',
  value = '',
  menuItems = [],
  itemValue = 'id',
  itemLabel = 'label',
  onChange,
  onOpen,
  onClose,
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const targetRef = useRef(null);
  const reservedIconSpace = useMemo(() => menuItems.some((item) => !!item.iconComponent), [
    menuItems,
  ]);

  const handleMenuClose = useCallback(
    (event) => {
      if (
        !targetRef?.current ||
        event.target === targetRef.current ||
        targetRef.current.contains(event.target)
      ) {
        return false;
      }
      setOpen(false);
      if (onClose) {
        onClose();
      }
    },
    [setOpen, onClose],
  );

  const handleMenuToggle = useCallback(() => {
    setOpen((open) => {
      if (open && onClose) {
        onClose();
      } else if (!open && onOpen) {
        onOpen();
      }

      return !open;
    });
  }, [setOpen, onOpen, onClose]);

  const handleItemClick = useCallback(
    (item) => {
      if (!item.disabled) {
        item.onClick && item.onClick(item[itemValue], item);
        onChange && onChange(item[itemValue], item);
        setOpen(false);
        if (onClose) {
          onClose();
        }
      }
    },
    [setOpen, itemValue, onChange, onClose],
  );

  return (
    <>
      {cloneElement(children, {
        ref: targetRef,
        onClick: handleMenuToggle,
      })}
      <Popper
        open={open}
        anchorEl={targetRef?.current}
        role="menu"
        transition
        placement={placement}
        disablePortal
        className={classes.menuRoot}>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom-start' ? 'center top' : 'center bottom',
            }}>
            <Paper elevation={4}>
              <ClickAwayListener mouseEvent="onMouseUp" onClickAway={handleMenuClose}>
                <MenuList
                  variant="selectedMenu"
                  className={classNames({
                    [classes.menuListWithHeader]: header,
                  })}>
                  {header ? (
                    <ListSubheader disableSticky className={classes.menuListHeader}>
                      {header}
                    </ListSubheader>
                  ) : null}
                  {menuItems.map((item, index) => {
                    const IconComponent = item.iconComponent;
                    const [ContentContainer, contentContainerProps] = item.path
                      ? [TPLink, { path: item.path, className: classes.linkContainer }]
                      : [React.Fragment, {}];

                    return (
                      <MenuItem
                        key={item[itemValue] || index}
                        selected={item[itemValue] === value || item.selected}
                        disabled={item.disabled}
                        onClick={() => handleItemClick(item)}
                        className={classNames(classes.menuItem, {
                          [classes.noGutters]: item.noGutters || item.path,
                          [classes.readonly]: item.readonly,
                        })}>
                        <ContentContainer {...contentContainerProps}>
                          {reservedIconSpace ? (
                            <ListItemIcon className={classes.iconContainer}>
                              {IconComponent ? <IconComponent fontSize="small" /> : null}
                            </ListItemIcon>
                          ) : null}
                          {item.renderItem ? (
                            item.renderItem(item)
                          ) : (
                            <span className={classes.text}>{item[itemLabel]}</span>
                          )}
                        </ContentContainer>
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

TPMenu.propTypes = {
  header: PropTypes.node,
  children: PropTypes.node,
  placement: PropTypes.oneOf([
    'top-start',
    'top',
    'top-end',
    'left-start',
    'left',
    'left-end',
    'right-start',
    'right',
    'right-end',
    'bottom-start',
    'bottom',
    'bottom-end',
    'auto-start',
    'auto',
    'auto-end',
  ]),
  value: PropTypes.string,
  onChange: PropTypes.func,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      path: PropTypes.string,
      iconComponent: PropTypes.elementType,
      label: PropTypes.string,
      renderItem: PropTypes.func,
      disabled: PropTypes.bool,
      noGutters: PropTypes.bool,
      onClick: PropTypes.func,
      readonly: PropTypes.bool,
    }),
  ).isRequired,
  /**
   * Key in menuItem object for value
   */
  itemValue: PropTypes.string,
  /**
   * Key in menuItem object for label
   */
  itemLabel: PropTypes.string,
};

export default TPMenu;
